import React from 'react';
import { Link } from 'react-router-dom';
import {
    FaHome, FaChevronRight, FaArrowRight, FaPhone, FaCheckCircle,
    FaHardHat, FaShieldAlt, FaBolt, FaClipboardCheck, FaUserShield,
    FaFireExtinguisher, FaExclamationTriangle, FaSearch, FaLock, FaTools
} from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const SafetyProfessionalsPage = () => {
    const services = [
        {
            icon: <FaUserShield size={32} />,
            title: 'On-Site Safety Management',
            description: 'Dedicated safety officers stationed on-site to enforce protocols, conduct daily safety briefings, and maintain a compliant work environment throughout your project.',
            gradient: 'from-blue-600 to-indigo-600'
        },
        {
            icon: <FaBolt size={32} />,
            title: 'Electrical Safety Oversight',
            description: 'Expert management of electrical hazards in live data center environments, including arc flash boundary enforcement, PPE compliance, and energized work permitting.',
            gradient: 'from-yellow-500 to-amber-600'
        },
        {
            icon: <FaSearch size={32} />,
            title: 'Hazard Assessment & Mitigation',
            description: 'Comprehensive job hazard analyses (JHA), pre-task planning, and site-specific risk assessments to identify and eliminate potential hazards before work begins.',
            gradient: 'from-orange-500 to-red-600'
        },
        {
            icon: <FaClipboardCheck size={32} />,
            title: 'OSHA Compliance Management',
            description: 'Full regulatory compliance oversight aligned with OSHA 29 CFR 1910 & 1926 standards, including documentation, recordkeeping, and audit readiness.',
            gradient: 'from-green-500 to-emerald-600'
        },
        {
            icon: <FaHardHat size={32} />,
            title: 'PPE Program Management',
            description: 'Oversight of Personal Protective Equipment programs including selection, inspection, training, and enforcement to ensure all personnel are properly protected at all times.',
            gradient: 'from-cyan-500 to-blue-600'
        },
        {
            icon: <FaLock size={32} />,
            title: 'Lockout/Tagout (LOTO) Oversight',
            description: 'Rigorous management of energy isolation procedures for maintenance and construction activities in live data center environments with zero tolerance for non-compliance.',
            gradient: 'from-slate-600 to-gray-800'
        },
        {
            icon: <FaFireExtinguisher size={32} />,
            title: 'Emergency Response Planning',
            description: 'Development and implementation of site-specific emergency action plans, evacuation procedures, and coordination with local emergency services.',
            gradient: 'from-red-600 to-rose-700'
        },
        {
            icon: <FaTools size={32} />,
            title: 'Safety Training & Certification',
            description: 'On-site and virtual safety training programs covering electrical safety, confined space entry, fall protection, first aid, and data center-specific hazard awareness.',
            gradient: 'from-violet-600 to-purple-700'
        },
        {
            icon: <FaExclamationTriangle size={32} />,
            title: 'Incident Reporting & Investigation',
            description: 'Prompt incident documentation, root cause analysis, and corrective action implementation to prevent recurrence and maintain a strong safety record.',
            gradient: 'from-teal-500 to-cyan-600'
        }
    ];

    const benefits = [
        'OSHA-Certified Safety Professionals',
        'Data Center-Specific Expertise',
        'Rapid Deployment Nationwide',
        'Electrical & Arc Flash Specialists',
        'Full Regulatory Compliance',
        'Zero-Incident Safety Culture',
        'Scalable Staffing Solutions',
        '24/7 Safety Support Coverage'
    ];

    const stats = [
        { value: '0', label: 'Lost-Time Incidents (Target)' },
        { value: '106+', label: 'Projects Supported' },
        { value: '100%', label: 'OSHA Compliance Rate' },
        { value: '115+', label: 'Sites Covered' }
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-500 rounded-full filter blur-3xl opacity-10 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl opacity-10 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <Link to="/services" className="hover:text-white transition-colors">Services</Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-yellow-400">Safety Professionals</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-block mb-4 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full">
                                <span className="text-yellow-400 text-sm font-bold tracking-wider">MISSION-CRITICAL SAFETY</span>
                            </div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                Expert <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-400">Safety Professionals</span> for Mission-Critical Data Centers
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Highly trained safety professionals dedicated to supporting data center operations, construction projects, maintenance activities, and critical infrastructure environments.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-yellow-500/30 transition-all duration-300">
                                    Request Safety Staff
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>

                        <div className="relative">
                            <div className="bg-gradient-to-br from-yellow-600/20 to-amber-600/20 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                                <img
                                    src="/serviceImages/safety-professionals-2.jpeg"
                                    alt="Expert Safety Professionals for Data Centers"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-10 border-t border-white/10">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-400">{stat.value}</div>
                                <div className="text-gray-400 text-sm mt-1">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-3xl p-12 border border-yellow-100">
                        <div className="grid lg:grid-cols-2 gap-10 items-center">
                            <div>
                                <span className="inline-block px-4 py-2 bg-yellow-100 text-yellow-700 rounded-full text-sm font-semibold mb-4">Who We Are</span>
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Safety is Essential in Every Data Center Environment</h2>
                                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                    From electrical hazards to operational compliance, maintaining a safe workplace is critical to protecting both people and infrastructure. We provide highly trained safety professionals dedicated to supporting data center operations, construction projects, maintenance activities, and critical infrastructure environments.
                                </p>
                            </div>
                            <div>
                                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                    Our experienced workforce helps organizations maintain the highest standards of workplace safety, compliance, and operational reliability.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    Whether you need a single safety officer for a short-term maintenance window or a full safety team for a large-scale construction project, we have the expertise and resources to support your mission-critical facility every step of the way.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-yellow-100 text-yellow-700 rounded-full text-sm font-semibold mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Comprehensive Safety Services</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Full-spectrum safety management tailored for mission-critical data center environments
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group relative bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-2xl hover:shadow-yellow-500/10 transition-all duration-500 hover:-translate-y-2"
                            >
                                <div className="p-8">
                                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                        {service.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-yellow-600 transition-colors">
                                        {service.title}
                                    </h3>
                                    <p className="text-gray-600 leading-relaxed mb-6">
                                        {service.description}
                                    </p>
                                    <Link
                                        to="/contact"
                                        className="inline-flex items-center gap-2 text-yellow-600 font-semibold group-hover:text-amber-600 transition-colors"
                                    >
                                        Learn More
                                        <FaArrowRight className="group-hover:translate-x-2 transition-transform" size={12} />
                                    </Link>
                                </div>
                                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${service.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <img
                                src="/serviceImages/safety-professionals.jpg"
                                alt="Safety Professionals at Work"
                                className="w-full h-auto rounded-2xl shadow-2xl object-cover"
                            />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-yellow-100 text-yellow-700 rounded-full text-sm font-semibold mb-4">Why Choose Us</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">Why Our Safety Professionals Stand Apart</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500 to-amber-600 flex items-center justify-center text-white flex-shrink-0 mt-0.5">
                                            <FaCheckCircle size={14} />
                                        </div>
                                        <span className="text-gray-700 font-medium">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-yellow-500/30 transition-all duration-300"
                            >
                                Get Safety Staff Today
                                <FaArrowRight size={14} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative bg-gradient-to-r from-gray-900 to-slate-800 py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img
                        src="/serviceImages/safety-professionals.jpg"
                        alt=""
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 to-slate-900/90"></div>
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full">
                        <FaShieldAlt className="text-yellow-400" size={14} />
                        <span className="text-yellow-400 text-sm font-semibold">Safety First. Always.</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">Ready to Protect Your Team & Facility?</h2>
                    <p className="text-xl text-gray-300 mb-12">
                        Contact us today to discuss your safety staffing needs. Our professionals are ready to deploy to your site.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-yellow-500 to-amber-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-yellow-500/30 transition-all duration-300"
                        >
                            Contact Us Today
                            <FaArrowRight size={14} />
                        </Link>
                        <a
                            href="tel:+12014099845"
                            className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300"
                        >
                            <FaPhone size={14} />
                            Call Us Now
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default SafetyProfessionalsPage;
